<section class="custom-modal" role="dialog" aria-modal="true" aria-labelledby="confirm-modal-title">
  <header class="modal-header">
    <app-display-icon class="modal-header-icon" [border]="false" [colour]="iconColourEnum.primary" [icon]="icons.checkCircleOutline"></app-display-icon>
    <div class="modal-header-text">
      <h2 id="confirm-modal-title" class="modal-title">{{ isRetryMode ? 'Confirm Retry' : 'Confirm Stock Take' }}</h2>
      @if (stockTakeName) {
        <span class="modal-subtitle">{{ stockTakeName }}</span>
      }
    </div>
    <button type="button" class="icon-button" aria-label="Close" (click)="onCancel()">
      <app-display-icon [border]="false" [colour]="iconColourEnum.primary" [icon]="icons.close"></app-display-icon>
    </button>
  </header>

  <div class="modal-body">
    <p class="modal-body-hint">
      @if (isRetryMode) {
        The following <strong>{{ countedItems.length }}</strong> item{{ countedItems.length === 1 ? '' : 's' }} will be sent to the server again for processing.
        Review the rand variances before confirming.
      } @else {
        The following <strong>{{ countedItems.length }}</strong> item{{ countedItems.length === 1 ? '' : 's' }} will be finalised.
        Review the rand variances before confirming.
      }
    </p>

    <div class="iq-variance-reminder" role="note">
      <app-display-icon
        class="iq-variance-reminder-icon"
        [border]="false"
        [colour]="iconColourEnum.yellow"
        [icon]="icons.infoSlabCircleOutline">
      </app-display-icon>
      <p class="iq-variance-reminder-text">
        Please print variance reports on IQ after the stock take has been processed to ensure stock adjustments are correct and have taken place.
        Let Gallix support know of any differences.
      </p>
    </div>

    @if (!isRetryMode && hasStockFileUpdates) {
      <div class="stock-file-updates" role="status">
        <p class="stock-file-updates-title">Stock file updates on process</p>
        <ul class="stock-file-updates-list">
          @if (pendingBarcodeUpdateCount > 0) {
            <li>
              <app-display-icon class="stock-file-updates-icon" [border]="false" [colour]="iconColourEnum.yellow" [icon]="icons.barcodeScan"></app-display-icon>
              <span><strong>{{ pendingBarcodeUpdateCount }}</strong> item{{ pendingBarcodeUpdateCount === 1 ? '' : 's' }} — barcode{{ pendingBarcodeUpdateCount === 1 ? '' : 's' }} will be updated</span>
            </li>
          }
          @if (binLocationUpdateCount > 0) {
            <li>
              <app-display-icon class="stock-file-updates-icon" [border]="false" [colour]="iconColourEnum.primary" [icon]="icons.archiveMarkerOutline"></app-display-icon>
              <span><strong>{{ binLocationUpdateCount }}</strong> item{{ binLocationUpdateCount === 1 ? '' : 's' }} — bin location{{ binLocationUpdateCount === 1 ? '' : 's' }} will be updated</span>
            </li>
          }
        </ul>
      </div>
    }

    @if (hasVarianceTotalChanged) {
      <div class="variance-total-changed" role="alert">
        <app-display-icon
          class="variance-total-changed-icon"
          [border]="false"
          [colour]="iconColourEnum.yellow"
          [icon]="icons.infoSlabCircleOutline">
        </app-display-icon>
        <div class="variance-total-changed-text">
          <p class="variance-total-changed-title">Variances have changed</p>
          <p class="variance-total-changed-body">
            Movement or del-col movement was updated during refresh. Total rand variance changed from
            <strong>R {{ preRefreshTotalRandVariance | number:'1.2-2' }}</strong>
            to
            <strong>R {{ totalRandVariance | number:'1.2-2' }}</strong>.
            Go back and review the updated variances in the grid before proceeding.
          </p>
        </div>
      </div>
    }

    @if (countedItems.length === 0) {
      <div class="empty-state">
        <app-display-icon [border]="false" [colour]="iconColourEnum.disabled" [icon]="icons.priceTags"></app-display-icon>
        <p>No counted items found. Count at least one item before processing.</p>
      </div>
    } @else {
      <div class="print-summary-sort">
        <span class="print-summary-sort-label" id="confirm-print-sort-label">Sort report by</span>
        <app-shared-custom-select
          aria-labelledby="confirm-print-sort-label"
          (selectionChange)="onSummarySortColumnChange($event)"
          [allowMultiple]="false"
          [allowSelectAll]="false"
          [isDisabled]="isGeneratingPdf()"
          [maxSelections]="1"
          [placeholder]="'Sort by'"
          [setOptions]="summarySortColumnOptions()"
          [showActionButtons]="false"
          [showChips]="false"
          [showSearchBar]="false">
        </app-shared-custom-select>
      </div>
      <div class="total-rand-variance">
        <span class="total-rand-variance-label">Total Rand Variance</span>
        <span
          class="total-rand-variance-value"
          [class.variance--negative]="totalRandVariance < 0"
          [class.variance--positive]="totalRandVariance > 0"
          [class.variance--neutral]="totalRandVariance === 0">
          R {{ totalRandVariance | number:'1.2-2' }}
        </span>
      </div>
      <div class="items-table">
        <div class="items-table-header">
          <span class="items-table-col items-table-col--code">Code</span>
          <span class="items-table-col items-table-col--desc">Description</span>
          <span class="items-table-col items-table-col--variance">Rand Variance</span>
        </div>
        <div class="items-table-body">
          @for (item of sortedCountedItems; track item.code) {
            <div class="items-table-row">
              <span class="items-table-col items-table-col--code">{{ item.code }}</span>
              <span class="items-table-col items-table-col--desc">{{ item.description || '—' }}</span>
              <span
                class="items-table-col items-table-col--variance"
                [class.variance--negative]="getRandVarianceValue(item) < 0"
                [class.variance--positive]="getRandVarianceValue(item) > 0"
                [class.variance--neutral]="getRandVarianceValue(item) === 0">
                R {{ getRandVarianceValue(item) | number:'1.2-2' }}
              </span>
            </div>
          }
        </div>
      </div>
    }
  </div>

  <footer class="modal-footer">
    <app-shared-button
      [colour]="'primary'"
      [disabled]="isGeneratingPdf()"
      (buttonClicked)="onCancel()">
      Cancel
    </app-shared-button>
    <app-shared-button
      class="print-summary-button"
      [fill]="false"
      [colour]="'secondary'"
      [disabled]="!canPrintSummary || isGeneratingPdf()"
      (buttonClicked)="onPrintSummary()">
      <app-display-icon
        [border]="false"
        [colour]="iconColourEnum.secondary"
        [icon]="icons.printerOutline">
      </app-display-icon>
      <span>{{ isGeneratingPdf() ? 'Generating PDF…' : 'Print Summary' }}</span>
    </app-shared-button>
    <app-shared-button
      class="confirm-process-button"
      [fill]="true"
      [disabled]="countedItems.length === 0 || isGeneratingPdf()"
      (buttonClicked)="onConfirm()">
      <app-display-icon
        [border]="false"
        [colour]="iconColourEnum.constant_white"
        [icon]="icons.checkCircleOutline">
      </app-display-icon>
      <span class="confirm-process-button__label">Confirm &amp; {{ isRetryMode ? 'Retry' : 'Process' }}</span>
    </app-shared-button>
  </footer>
</section>
